import React, { useCallback, useMemo, useState, createContext } from 'react';
import { HomePage } from './pages/HomePage';
import { ShopPage } from './pages/ShopPage';
import { ProductPage } from './pages/ProductPage';
import { CartPage } from './pages/CartPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { OrderConfirmationPage } from './pages/OrderConfirmationPage';
import { CollectionsPage } from './pages/CollectionsPage';
import { AboutPage } from './pages/AboutPage';
import { ContactPage } from './pages/ContactPage';
export type Page = 'home' | 'shop' | 'product' | 'cart' | 'checkout' | 'confirmation' | 'collections' | 'about' | 'contact';
export interface CartItem {
  id: number;
  name: string;
  price: number;
  size: string;
  color: string;
  quantity: number;
  image: string;
}
interface CartContextType {
  cart: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (id: number, size: string, color: string) => void;
  updateQuantity: (id: number, size: string, color: string, quantity: number) => void;
  clearCart: () => void;
  cartCount: number;
  cartTotal: number;
}
interface NavigationContextType {
  navigate: (page: Page) => void;
  currentPage: Page;
}
export const NavigationContext = createContext<NavigationContextType>({
  navigate: () => {},
  currentPage: 'home'
});
export const CartContext = createContext<CartContextType>({
  cart: [],
  addToCart: () => {},
  removeFromCart: () => {},
  updateQuantity: () => {},
  clearCart: () => {},
  cartCount: 0,
  cartTotal: 0
});
export function App() {
  const [currentPage, setCurrentPage] = useState<Page>('home');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [notification, setNotification] = useState<string | null>(null);
  const navigate = useCallback((page: Page) => {
    setCurrentPage(page);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);
  const showNotification = (message: string) => {
    setNotification(message);
    setTimeout(() => setNotification(null), 2500);
  };
  const addToCart = useCallback((item: CartItem) => {
    setCart(prev => {
      const existingIndex = prev.findIndex(i => i.id === item.id && i.size === item.size && i.color === item.color);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex].quantity += item.quantity;
        return updated;
      }
      return [...prev, item];
    });
    showNotification(`${item.name} added to cart`);
  }, []);
  const removeFromCart = useCallback((id: number, size: string, color: string) => {
    setCart(prev => prev.filter(item => !(item.id === id && item.size === size && item.color === color)));
  }, []);
  const updateQuantity = useCallback((id: number, size: string, color: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(id, size, color);
      return;
    }
    setCart(prev => prev.map(item => item.id === id && item.size === size && item.color === color ? {
      ...item,
      quantity
    } : item));
  }, [removeFromCart]);
  const clearCart = useCallback(() => {
    setCart([]);
  }, []);
  const cartCount = useMemo(() => cart.reduce((sum, item) => sum + item.quantity, 0), [cart]);
  const cartTotal = useMemo(() => cart.reduce((sum, item) => sum + item.price * item.quantity, 0), [cart]);
  const navigationValue = useMemo(() => ({
    navigate,
    currentPage
  }), [navigate, currentPage]);
  const cartValue = useMemo(() => ({
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    cartCount,
    cartTotal
  }), [cart, addToCart, removeFromCart, updateQuantity, clearCart, cartCount, cartTotal]);
  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return <HomePage data-id="element-0" />;
      case 'shop':
        return <ShopPage data-id="element-1" />;
      case 'product':
        return <ProductPage data-id="element-2" />;
      case 'cart':
        return <CartPage data-id="element-3" />;
      case 'checkout':
        return <CheckoutPage data-id="element-4" />;
      case 'confirmation':
        return <OrderConfirmationPage data-id="element-5" />;
      case 'collections':
        return <CollectionsPage data-id="element-6" />;
      case 'about':
        return <AboutPage data-id="element-7" />;
      case 'contact':
        return <ContactPage data-id="element-8" />;
      default:
        return <HomePage data-id="element-9" />;
    }
  };
  return <NavigationContext.Provider value={navigationValue} data-id="element-10">
      <CartContext.Provider value={cartValue} data-id="element-11">
        {renderPage()}

        {/* Toast Notification */}
        {notification && <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-fade-in-up" data-id="element-12">
            <div className="bg-[#1A1A1A] text-[#D4A853] px-6 py-3 rounded shadow-lg font-['Raleway'] text-sm" data-id="element-13">
              {notification}
            </div>
          </div>}
      </CartContext.Provider>
    </NavigationContext.Provider>;
}